import { useEffect, type ReactNode } from 'react'
import {
  Code as CodeIcon,
  FileText,
  ListOrdered,
  ScrollText,
} from 'lucide-react'
import { LowerPanel, UpgradeOverlay, type LowerPanelTab } from '@/components'
import BotBacktestCode from './BotBacktestCode'
import type { BotCode } from '../api'

export type BottomTab =
  | 'results'
  | 'trades'
  | 'logs'
  | 'code'

interface BacktestBottomPanelProps {
  tradesContent?: ReactNode
  newResultsContent?: ReactNode
  logsContent?: ReactNode
  currentCode?: BotCode | null
  hasCode?: boolean
  activeTab?: BottomTab
  onActiveTabChange?: (tab: BottomTab) => void
  isMaximized: boolean
  onToggleMaximize: () => void
  /** When true, free-plan users see a blurred upgrade overlay over the tab content. */
  showUpgradeLock?: boolean
}

const ICON_SIZE = 11

interface TabSpec {
  id: BottomTab
  label: string
  icon: ReactNode
  badgeSlot?: ReactNode
}

export default function BacktestBottomPanel({
  tradesContent,
  newResultsContent,
  logsContent,
  currentCode,
  hasCode = false,
  activeTab: controlledActiveTab,
  onActiveTabChange,
  isMaximized,
  onToggleMaximize,
  showUpgradeLock = false,
}: BacktestBottomPanelProps) {
  const activeTab = controlledActiveTab ?? 'results'

  const setActiveTab = (tab: BottomTab) => {
    onActiveTabChange?.(tab)
  }

  const codeBadge = currentCode?.version != null ? (
    <span className="font-mono tabular-nums opacity-70">
      v{currentCode.version}
    </span>
  ) : null

  const allTabs: TabSpec[] = [
    {
      id: 'results',
      label: 'Results',
      icon: <ScrollText size={ICON_SIZE} strokeWidth={1.5} />,
    },
    {
      id: 'trades',
      label: 'Trades',
      icon: <ListOrdered size={ICON_SIZE} strokeWidth={1.5} />,
    },
    {
      id: 'logs',
      label: 'Logs',
      icon: <FileText size={ICON_SIZE} strokeWidth={1.5} />,
    },
    {
      id: 'code',
      label: 'Code',
      icon: <CodeIcon size={ICON_SIZE} strokeWidth={1.5} />,
      badgeSlot: codeBadge,
    },
  ]

  const visibleTabs: LowerPanelTab[] = allTabs.map((t) => ({
    id: t.id,
    label: t.label,
    icon: t.icon,
    badgeSlot: t.badgeSlot,
  }))

  // Fall back to the first visible tab if the controlled active tab disappears.
  useEffect(() => {
    if (visibleTabs.some((t) => t.id === activeTab)) return
    if (visibleTabs.length === 0) return
    setActiveTab(visibleTabs[0].id as BottomTab)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  return (
    <LowerPanel
      tabs={visibleTabs}
      activeTabId={activeTab}
      onTabChange={(id) => setActiveTab(id as BottomTab)}
      expanded={isMaximized}
      onExpandUp={isMaximized ? undefined : onToggleMaximize}
      onCollapseDown={isMaximized ? onToggleMaximize : undefined}
    >
      <div className="h-full min-h-0 relative">
        {activeTab === 'results' && (
          <div className="h-full min-h-0 overflow-auto">{newResultsContent}</div>
        )}

        {activeTab === 'trades' && (
          <div className="h-full min-h-0">{tradesContent}</div>
        )}

        {activeTab === 'logs' && (
          <div className="h-full min-h-0">{logsContent}</div>
        )}

        {activeTab === 'code' && (
          <div className="h-full min-h-0 overflow-hidden">
            <BotBacktestCode
              key={currentCode?.cid ?? 'no-code'}
              currentCode={currentCode}
              hasCode={hasCode}
            />
          </div>
        )}

        {/* The Code tab shows the strategy code regardless of any backtest, so
            it locks whenever code exists. The other tabs only lock once there
            are real (completed) backtest results. UpgradeOverlay self-gates to
            free-plan users, so these conditions only decide tab/message. The
            heavier blur + grayscale obscures the locked content more. */}
        {activeTab === 'code'
          ? hasCode && (
              <UpgradeOverlay
                message="Upgrade to see your strategy code"
                className="backdrop-grayscale"
              />
            )
          : showUpgradeLock && (
              <UpgradeOverlay
                message="Upgrade to see your backtest results"
                className="backdrop-grayscale"
              />
            )}
      </div>
    </LowerPanel>
  )
}
